import type { Edge, Node } from 'reactflow';

import { BotModuleData, NodeType } from '@/domain/bot';
import { getHandlesFromEdges } from 'utils/reactflow/get-handles-from-edges';

/**
 * Transforms data format used to store modules at fronted (nodes and edges) to modules used in database (mappie)
 * @param nodes - array containg data about nodes used on frontend - translated to module params
 * @param edges - array containg data about edges used on frontend - translated to module handlers
 * @returns modules - object representing modules in database (mappie)
 */

const buildHandlers = (
    node: Node,
    edges: Edge[],
    completeHandles?: { [x: string]: any },
) => {
    let handlersObject: { [x: string]: string | undefined | null } | undefined;

    const nodeHandles = getHandlesFromEdges(node.id, edges);

    if (node?.data?.type === 'CustomInternalModule' && completeHandles) {
        const expectedHandles = completeHandles[node.id] ?? [];
        handlersObject = {};
        expectedHandles.forEach((handle: string) => {
            const hasEdge = edges.some(
                (edge) => edge.source === node.id && edge.id.endsWith(handle),
            );

            if (!hasEdge && handlersObject) {
                handlersObject[`on_${handle}`] = '';
            }
        });
    }

    switch (node?.data?.type) {
        case 'Exit':
            handlersObject = undefined;
            break;
        case 'YesNoConfirmation':
            handlersObject = {
                node_exit: getModuleHandlers(edges, node.id, 'Y'),
                on_no: getModuleHandlers(edges, node.id, 'N'),
                on_fallback: getModuleHandlers(edges, node.id, 'F'),
                on_escape: getModuleHandlers(edges, node.id, 'S'),
            };
            break;
        case 'GPTModule':
            handlersObject = {
                node_exit: getModuleHandlers(edges, node.id, 'E'),
                on_timeout_exception: getModuleHandlers(edges, node.id, 'M'),
                on_content_filter_exception: getModuleHandlers(
                    edges,
                    node.id,
                    'L',
                ),
                on_exception: getModuleHandlers(edges, node.id, 'C'),
            };
            break;
        case 'MSearchModule':
            handlersObject = {
                node_exit: getModuleHandlers(edges, node.id, 'E'),
                on_error: getModuleHandlers(edges, node.id, 'C'),
                on_no_result_found: getModuleHandlers(edges, node.id, 'L'),
            };
        case 'NumericRatingModule':
            handlersObject = {
                node_exit: getModuleHandlers(edges, node.id, 'E'),
                on_fallback: getModuleHandlers(edges, node.id, 'F'),
            };
            break;
        case 'ValueRangeRouter':
            const numOfIntervals =
                node?.data?.params?.interval_partition_points?.length + 1;
            handlersObject = {};

            for (let i = 0; i < numOfIntervals; i++) {
                handlersObject[`on_value_in_range_${i}`] = getModuleHandlers(
                    edges,
                    node.id,
                    `${i}`,
                );
            }
            handlersObject['on_value_not_set'] = getModuleHandlers(
                edges,
                node.id,
                'N',
            );
            handlersObject['on_value_out_of_bounds'] = getModuleHandlers(
                edges,
                node.id,
                'O',
            );
            break;
        case 'TransferToAgentModule':
            handlersObject = {
                on_agent_not_available: getModuleHandlers(edges, node.id, 'A'),
                on_message_received: getModuleHandlers(edges, node.id, 'R'),
                on_message_denied: getModuleHandlers(edges, node.id, 'D'),
            };
            break;
        case 'CustomInternalModule':
            handlersObject = handlersObject || {};

            nodeHandles.forEach((handle) => {
                if (
                    handlersObject &&
                    handlersObject[`on_${handle}`] === undefined
                ) {
                    handlersObject[`on_${handle}`] = getModuleHandlers(
                        edges,
                        node.id,
                        handle,
                    );
                }
            });
            break;
        default:
            handlersObject = {
                node_exit: getModuleHandlers(edges, node.id, 'E'),
            };
    }

    return handlersObject;
};

export const getModuleTypeHandlers = (
    nodeType: string | undefined,
    node?: Node,
    nodeHandles?: Record<string, string[]> | undefined,
): string[] => {
    switch (nodeType) {
        case 'Exit':
            return [];
        case 'YesNoConfirmation': {
            const handlers = ['Y', 'N', 'F'];
            const hasEscapeHandleFromParams =
                // @ts-ignore - BotModuleData union type complexity
                node?.data?.params?.escape_mode !== undefined;

            if (hasEscapeHandleFromParams) {
                handlers.push('S');
            }

            return handlers;
        }
        case 'NumericRatingModule':
            return ['E', 'F'];
        case 'ValueRangeRouter':
            const numOfIntervals = node?.data
                ? node?.data?.params?.interval_partition_points.length + 1
                : 1;
            const indedexedPorts = Array.from(
                {
                    length: numOfIntervals,
                },
                (_, index) => String(index),
            );
            indedexedPorts.push('N');
            indedexedPorts.push('O');
            return indedexedPorts;
        case 'GPTModule':
            return ['E', 'M', 'L', 'C'];
        case 'MSearchModule':
            return ['E', 'C', 'L'];
        case 'TransferToAgentModule':
            return ['A', 'R', 'D'];
        case 'CustomInternalModule':
            if (nodeHandles && node && node.id && nodeHandles[node.id]) {
                return nodeHandles[node.id];
            }
            return ['0'];
        default:
            return ['E'];
    }
};

export const flowDataToModules = (
    nodes: Node<BotModuleData>[],
    edges: Edge[],
    completeHandles?: { [x: string]: any } | undefined,
) => {
    const modules: { [key: string]: any } = {};

    for (const node of nodes) {
        if (node.id !== 'start') {
            const nodeData = node.data;

            const rawParams =
                // @ts-ignore - BotModuleData union type complexity
                nodeData.type === 'Exit' ? undefined : nodeData.params;
            const params =
                nodeData.type === 'YesNoConfirmation' ||
                nodeData.type === 'ValueRangeRouter'
                    ? {
                          ...rawParams,
                          escape_mode:
                              rawParams?.escape_mode === 'undefined'
                                  ? undefined
                                  : rawParams?.escape_mode,
                      }
                    : rawParams;

            modules[node.id] = {
                type:
                    nodeData.type === 'CustomInternalModule'
                        ? nodeData.typeOverride
                        : nodeData.type,
                params,
                ...('source' in nodeData ? { source: nodeData.source } : {}),
                // problem with type - any of keys in handlers can be undefined here
                handlers: buildHandlers(node, edges, completeHandles),
            };
        }
    }

    return modules;
};

const getModuleHandlers = (
    edges: Edge[],
    startString: string,
    endString: string,
) => {
    const edge = edges.find(
        (e) => e.source === startString && e.id.endsWith(endString),
    );

    return edge?.target;
};
